import { StatusBar, Style } from "@capacitor/status-bar";
import { Capacitor } from "@capacitor/core";
import { LogService } from "@bitwarden/common/abstractions/log.service";

// LogService instance - set once during app initialization
let logService: LogService;

/**
 * Set the LogService instance for this module
 * Should be called once during app initialization
 */
export function setStatusBarLogService(service: LogService): void {
  logService = service;
}

/**
 * Initialize status bar styling to match the effective app theme
 * Should be called on app startup and whenever the effective theme changes
 */
export async function initializeStatusBar(isDark = false): Promise<void> {
  if (!Capacitor.isNativePlatform()) {
    return;
  }

  try {
    // Style.Dark = light icons for a dark page, Style.Light = dark icons for a light page
    await StatusBar.setStyle({ style: isDark ? Style.Dark : Style.Light });

    // Match the app background (no effect when the native shell draws edge-to-edge)
    await StatusBar.setBackgroundColor({ color: isDark ? "#111113" : "#F0F0F3" });

    // Show the status bar if hidden
    await StatusBar.show();
  } catch (error) {
    if (logService) {
      logService.error("[StatusBar] Failed to initialize: " + error);
    }
  }
}

/**
 * Set status bar style
 */
export async function setStatusBarStyle(style: Style): Promise<void> {
  if (!Capacitor.isNativePlatform()) {
    return;
  }

  try {
    await StatusBar.setStyle({ style });
  } catch (error) {
    if (logService) {
      logService.error("[StatusBar] Failed to set style: " + error);
    }
  }
}

/**
 * Set status bar background color
 */
export async function setStatusBarColor(color: string): Promise<void> {
  if (!Capacitor.isNativePlatform()) {
    return;
  }

  try {
    await StatusBar.setBackgroundColor({ color });
  } catch (error) {
    if (logService) {
      logService.error("[StatusBar] Failed to set color: " + error);
    }
  }
}

/**
 * Hide status bar (use carefully - typically for fullscreen content)
 */
export async function hideStatusBar(): Promise<void> {
  if (!Capacitor.isNativePlatform()) {
    return;
  }

  try {
    await StatusBar.hide();
  } catch (error) {
    if (logService) {
      logService.error("[StatusBar] Failed to hide: " + error);
    }
  }
}

/**
 * Show status bar
 */
export async function showStatusBar(): Promise<void> {
  if (!Capacitor.isNativePlatform()) {
    return;
  }

  try {
    await StatusBar.show();
  } catch (error) {
    if (logService) {
      logService.error("[StatusBar] Failed to show: " + error);
    }
  }
}
